import { createFileRoute, redirect, Link } from '@tanstack/react-router';
import { motion } from 'motion/react';
import { z } from 'zod';
import { useState } from 'react';
import { useArticles } from '@/queries/articles';
import { ArticleList } from '@/components/ArticleList';
import { CircularLoader } from '@/components/CircularLoader';
import PlusIcon from '@/assets/common/plus.svg?react';
import SearchIcon from '@/assets/common/search.svg?react';
import styles from './articles.module.scss';

const searchSchema = z.object({
  tab: z.enum(['my-articles', 'shared']).optional().default('my-articles'),
});

export const Route = createFileRoute('/articles/')({
  component: ArticlesPage,
  validateSearch: searchSchema,
  beforeLoad: ({ context }) => {
    const { user, authLoading } = context as { user: any; authLoading: boolean };
    if (authLoading) return;
    if (!user) {
      throw redirect({ to: '/' });
    }
  },
});

function ArticlesPage() {
  const { tab } = Route.useSearch();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: articles, isLoading, error } = useArticles({
    search: searchTerm || undefined,
  });

  // Filter articles based on tab
  const filteredArticles = articles?.filter(article => {
    if (tab === 'shared') {
      // Show articles shared with me (not my own)
      return article.user_id !== article.author?.user_id;
    }
    // Show my own articles
    return true;
  }) || [];

  return (
    <div className={styles.articles}>
      <div className={styles.articles__header}>
        <div className={styles.articles__headerContent}>
          <h1 className={styles.articles__title}>Articles</h1>
          <p className={styles.articles__subtitle}>
            Create and share articles with markdown support
          </p>
        </div>

        <Link to="/articles/create" className={styles.articles__createButton}>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={styles.articles__createButtonInner}
          >
            <PlusIcon />
            New Article
          </motion.button>
        </Link>
      </div>

      <div className={styles.articles__tabs}>
        <Link
          to="/articles"
          search={{ tab: 'my-articles' }}
          className={`${styles.articles__tab} ${tab === 'my-articles' ? styles['articles__tab--active'] : ''}`}
        >
          My Articles
        </Link>
        <Link
          to="/articles"
          search={{ tab: 'shared' }}
          className={`${styles.articles__tab} ${tab === 'shared' ? styles['articles__tab--active'] : ''}`}
        >
          Shared with Me
        </Link>
      </div>

      <div className={styles.articles__search}>
        <SearchIcon className={styles.articles__searchIcon} />
        <input
          type="text"
          placeholder="Search articles..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.articles__searchInput}
        />
      </div>

      {isLoading ? (
        <div className={styles.articles__loading}>
          <CircularLoader />
        </div>
      ) : error ? (
        <div className={styles.articles__error}>
          Error loading articles: {error.message}
        </div>
      ) : (
        <ArticleList
          articles={filteredArticles}
          emptyMessage={
            tab === 'shared'
              ? "No articles have been shared with you yet"
              : "You haven't created any articles yet. Click 'New Article' to get started!"
          }
        />
      )}

      <div className={styles.articles__browseLink}>
        <Link to="/articles/public">
          Browse public articles from the community
        </Link>
      </div>
    </div>
  );
}
